"use client"

import type React from "react"
import { memo } from "react"
import { ChevronDown, ChevronRight } from "lucide-react"
import type { SyncSection , ConnectorData} from "../../../../types/edit-connector-wizard"

interface SyncRulesStepProps {
  connectorData: ConnectorData
  syncRules: SyncSection[]
  onToggleRule: (sectionId: string, ruleId: string) => void
  onToggleSection: (sectionId: string) => void
}

const SyncRulesStep: React.FC<SyncRulesStepProps> = memo(({ connectorData, syncRules, onToggleRule, onToggleSection }) => {
  const getPlatformIcon = (platform: string) => {
    if (platform === "hubspot") {
      return (
        <div className="platform-logo hubspot">
            <img src="https://cdn.worldvectorlogo.com/logos/hubspot-1.svg" alt="HubSpot" />
        </div>
      )
    }
    return (
      <div className="platform-logo salesforce">
        <img src="https://cdn.worldvectorlogo.com/logos/salesforce-2.svg" alt="Salesforce"  />
      </div>
    )
  }

  const getObjectName = (name: string, isLower:boolean, isWord : boolean,) =>{
    let objectName = ""
    if(isWord){
      objectName = name && name === 'source_object' ? connectorData.source_object || 'contact' : name && name === 'target_object' ? connectorData.target_object || 'lead': 'contact';
    }else{
      
      objectName = name.includes('contact') ? name.replace('contact',connectorData.source_object.replace(connectorData.source_object.charAt(0),connectorData.source_object.charAt(0).toLowerCase())) :  name.replace('lead', connectorData.target_object.replace(connectorData.target_object.charAt(0),connectorData.target_object.charAt(0).toLowerCase()))
    }
    return isLower ? objectName.replace(objectName.charAt(0),objectName.charAt(0).toUpperCase()) : objectName.replace(objectName.charAt(0),objectName.charAt(0).toLowerCase())
  }


  return (
    <div className="sync-rules-step">
      <div className="step-header">
        <h1>Configure Sync Rules</h1>
      </div>

      <div className="sync-overview">
        <div className="platform-overview">
          <div className="platform-item">
            {getPlatformIcon("hubspot")}
            <span>HubSpot { getObjectName('source_object', false, true) }</span>
          </div>

          <div className="sync-arrows-horizontal">
            <div className="direction-arrow bidirectional">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 11l4 4-4 4" />
                <path d="M7 13l-4-4 4-4" />
                <path d="M21 15H3" />
                <path d="M21 9H3" />
              </svg>
            </div>
          </div>

          <div className="platform-item">
            {getPlatformIcon("salesforce")}
            <span>Salesforce { getObjectName('target_object', false, true) }</span>
          </div>

          <div className="sync-status">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#10B981">
              <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
          </div>
        </div>

        <p className="sync-description">Two-way sync of Salesforce { getObjectName('target_object', false, true) } and contacts to HubSpot { getObjectName('source_object', false, true) }.</p>
      </div>

      <div className="sync-sections">
        {syncRules.map((section) => (
          <div key={section.id} className="sync-section">
            <button className="section-header" onClick={() => onToggleSection(section.id)}>
              <div className="section-title">
                <span className={`expand-icon ${section.expanded ? "expanded" : ""}`}>
                  {section.expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </span>
                <h3>{section.title}</h3>
              </div>
            </button>

            {section.expanded && (
              <div className="section-content">
                <div className="rules-header">
                  <div className="source-header">Source</div>
                  <div className="target-header">Target</div>
                  <div className="toggle-header"></div>
                </div>

                <div className="rules-list">
                  {section.rules.map((rule) => (
                    <div key={rule.id} className={`rule-row ${rule.enabled ? "enabled" : "disabled"}`}>
                      <div className="rule-source">
                        {getPlatformIcon(rule.sourceIcon)}
                        <span className="rule-text">{ getObjectName(rule.sourceAction, true, false) }</span>
                      </div>

                      <div className="rule-arrow">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M5 12h14" />
                          <path d="M12 5l7 7-7 7" />
                        </svg>
                      </div>

                      <div className="rule-target">
                        {getPlatformIcon(rule.targetIcon)}
                        <span className="rule-text">{getObjectName(rule.targetAction, true, false)}</span>
                      </div>

                      <div className="rule-toggle">
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={rule.enabled}
                            onChange={() => onToggleRule(section.id, rule.id)}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
})

SyncRulesStep.displayName = "SyncRulesStep"

export default SyncRulesStep
